import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridReadyEvent, SelectionChangedEvent, CellContextMenuEvent, ValueSetterParams } from 'ag-grid-enterprise';
import { useNavigate } from 'react-router-dom';
import { ChemFile, Project } from '../../types/chemistry';
import { useChemistryStore } from '../../stores/chemistryStore';
import { useUIStore } from '../../stores/uiStore';
import { chemistryApi } from '../../api/chemistry';
import apiClient from '../../api/client';
import { formatDate } from '../../utils/dateFormat';
import { DATA_POINT_DESCRIPTION, formatDataCount } from '../../utils/fileDataSummary';


/** Ensure the renamed file keeps its original extension if user omits it */
const ensureExtension = (newName: string, originalFileName: string): string => {
  const origExt = originalFileName.split('.').pop()?.toLowerCase() || '';
  if (!origExt) return newName;
  const newExt = newName.split('.').pop()?.toLowerCase() || '';
  if (newExt === origExt) return newName;
  return `${newName}.${origExt}`;
};

interface FileListProps {
  files: ChemFile[];
  onSelectionChange?: (selectedIds: string[]) => void;
  showMatchCount?: boolean;  // Show Matches column for search results
  searchQuery?: string;      // For highlighting matches in editor
  searchType?: string;       // substructure, similarity, exact
  disabled?: boolean;        // Disable interactions during background tasks
  lockedFileIds?: string[];  // Files currently being processed (shown as locked/grayed)
  currentProjectId?: number | null;  // Current project filter (to exclude from Move menu)
  onFileMoved?: () => void;  // Callback when files are moved (to refresh list)
}

const FileList: React.FC<FileListProps> = ({
  files,
  onSelectionChange,
  showMatchCount = false,
  searchQuery,
  searchType,
  disabled = false,
  lockedFileIds = [],
  currentProjectId,
  onFileMoved,
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const navigate = useNavigate();
  const { setSelectedFileIds, selectedFileIds } = useChemistryStore();
  const openDedupDialog = useUIStore((s) => s.openDedupDialog);
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; fileId: string; fileName: string } | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isMoving, setIsMoving] = useState(false);

  // Rename state
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Fetch projects for "Move to..." menu
  useEffect(() => {
    chemistryApi.getProjects().then(setProjects).catch(console.error);
  }, []);

  // Helper to check if a file is locked
  const isFileLocked = useCallback((fileId: string) => lockedFileIds.includes(fileId), [lockedFileIds]);

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(null);
      setIsRenaming(false);
      setRenameValue('');
    };
    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  // Handle right-click on row
  const onCellContextMenu = useCallback((event: CellContextMenuEvent) => {
    event.event?.preventDefault();
    const mouseEvent = event.event as MouseEvent;
    if (mouseEvent && event.data && !isFileLocked(event.data.id)) {
      setContextMenu({
        x: mouseEvent.clientX,
        y: mouseEvent.clientY,
        fileId: event.data.id,
        fileName: event.data.originalFileName || event.data.fileName || '',
      });
    }
  }, [isFileLocked]);

  // Move file to project
  const handleMoveToProject = useCallback(async (projectId: number) => {
    if (!contextMenu) return;
    setIsMoving(true);
    try {
      await chemistryApi.bulkMoveFiles([contextMenu.fileId], projectId);
      setContextMenu(null);
      onFileMoved?.();
    } catch (err) {
      console.error('Failed to move file:', err);
    } finally {
      setIsMoving(false);
    }
  }, [contextMenu, onFileMoved]);

  // Cancel upload handler
  const handleCancelUpload = useCallback(async (fileId: string) => {
    setCancellingIds(prev => new Set(prev).add(fileId));
    try {
      await chemistryApi.cancelUpload(fileId);
    } catch (err) {
      console.error('Failed to cancel upload:', err);
    } finally {
      setCancellingIds(prev => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  }, []);

  // Start rename flow
  const handleStartRename = useCallback(() => {
    if (!contextMenu) return;
    // Strip UUID prefix for editing
    const cleanName = contextMenu.fileName.replace(/^[a-f0-9]{8}_/i, '');
    setRenameValue(cleanName);
    setIsRenaming(true);
    // Focus input after render
    setTimeout(() => renameInputRef.current?.focus(), 50);
  }, [contextMenu]);

  // Submit rename
  const handleRenameSubmit = useCallback(async () => {
    if (!contextMenu || !renameValue.trim()) return;
    try {
      const finalName = ensureExtension(renameValue.trim(), contextMenu.fileName);
      await chemistryApi.renameFile(contextMenu.fileId, finalName);
      setContextMenu(null);
      setIsRenaming(false);
      setRenameValue('');
      onFileMoved?.(); // Refresh the file list
    } catch (err) {
      console.error('Failed to rename file:', err);
    }
  }, [contextMenu, renameValue, onFileMoved]);

  // Cancel rename
  const handleRenameCancel = useCallback(() => {
    setIsRenaming(false);
    setRenameValue('');
  }, []);

  // Download a file in the given format
  const [isContextDownloading, setIsContextDownloading] = useState(false);
  const handleContextDownload = useCallback(async (fileId: string, fileName: string, format: string) => {
    setIsContextDownloading(true);
    setContextMenu(null);
    try {
      const response = await apiClient.post('/export/molecules', {
        file_id: Number(fileId),
        molecule_ids: [],
        format,
      }, { responseType: 'blob' });

      const baseName = fileName.replace(/^[a-f0-9]{8}_/i, '').replace(/\.[^.]+$/, '');
      const extMap: Record<string, string> = { sdf: '.sdf.gz', csv: '.csv', smi: '.smi' };
      const filename = baseName + (extMap[format] || `.${format}`);

      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setIsContextDownloading(false);
    }
  }, []);

  const columnDefs: ColDef[] = useMemo(
    () => {
      const cols: ColDef[] = [
        // Checkbox column - using AG Grid's built-in selection checkbox
        {
          colId: 'checkbox',
          headerName: '',
          width: 50,
          minWidth: 50,
          maxWidth: 50,
          pinned: 'left',
          sortable: false,
          filter: false,
          resizable: false,
          suppressMovable: true,
          suppressHeaderMenuButton: true,
          checkboxSelection: true,
          headerCheckboxSelection: true,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          field: 'originalFileName',
          headerName: 'Name',
          flex: 2,
          minWidth: 200,
          sortable: true,
          filter: true,
          editable: (params) => !disabled && !isFileLocked(params.data?.id),
          valueSetter: (params: ValueSetterParams) => {
            const newValue = params.newValue?.trim();
            const oldValue = params.oldValue;
            // Don't update if value hasn't changed or is empty
            if (!newValue || newValue === oldValue) {
              return false;
            }
            // Auto-append original extension if user omitted it
            const finalName = ensureExtension(newValue, params.data.fileName || '');
            // Call rename API asynchronously
            chemistryApi.renameFile(params.data.id, finalName)
              .then(() => {
                // Update the local data
                params.data.originalFileName = finalName;
                // Refresh the file list to get updated data
                onFileMoved?.();
              })
              .catch((err) => {
                console.error('Failed to rename file:', err);
              });
            // Return true to indicate value was set (optimistically)
            return true;
          },
          cellRenderer: (params: any) => {
            // Strip UUID prefix pattern (8 hex chars + underscore) from filename display
            const rawName = params.value || params.data?.fileName || '';
            const cleanName = rawName.replace(/^[a-f0-9]{8}_/i, '');
            const lowerName = rawName.toLowerCase();

            // Determine icon style by file extension
            let iconBg = 'from-emerald-100 to-teal-50';
            let iconColor = 'text-emerald-600';
            let iconPath = 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
            let iconLabel = '';

            if (lowerName.endsWith('.rxn') || lowerName.endsWith('.rdf')) {
              iconBg = 'from-purple-100 to-violet-50';
              iconColor = 'text-purple-600';
              iconPath = 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4';
            } else if (lowerName.endsWith('.csv')) {
              iconBg = 'from-blue-100 to-sky-50';
              iconColor = 'text-blue-600';
              iconLabel = 'CSV';
            } else if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls')) {
              iconBg = 'from-green-100 to-emerald-50';
              iconColor = 'text-green-700';
              iconLabel = 'XLS';
            } else if (lowerName.endsWith('.pptx')) {
              iconBg = 'from-orange-100 to-amber-50';
              iconColor = 'text-orange-600';
              iconLabel = 'PPT';
            } else if (lowerName.endsWith('.fasta') || lowerName.endsWith('.fa') || lowerName.endsWith('.faa') || lowerName.endsWith('.fna')) {
              iconBg = 'from-indigo-100 to-blue-50';
              iconColor = 'text-indigo-600';
              iconPath = 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z';
            } else if (lowerName.endsWith('.pdb')) {
              iconBg = 'from-violet-100 to-purple-50';
              iconColor = 'text-violet-600';
              iconPath = 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z';
            } else if (lowerName.endsWith('.smi') || lowerName.endsWith('.smiles')) {
              iconBg = 'from-teal-100 to-cyan-50';
              iconColor = 'text-teal-600';
              iconLabel = 'SMI';
            }

            return (
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 bg-gradient-to-br ${iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  {iconLabel ? (
                    <span className={`text-[10px] font-bold ${iconColor}`}>{iconLabel}</span>
                  ) : (
                    <svg className={`w-5 h-5 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={iconPath} />
                    </svg>
                  )}
                </div>
                <span
                  className="font-medium text-gray-800 hover:text-emerald-600 cursor-pointer transition-colors truncate"
                  title={rawName}
                >
                  {cleanName}
                </span>
              </div>
            );
          },
        },
      ];

      // Add Matches column for search results (before other columns)
      if (showMatchCount) {
        cols.push({
          field: 'matchCount',
          headerName: 'Matches',
          width: 120,
          sortable: true,
          sort: 'desc',  // Sort by matches descending by default
          type: 'numericColumn',
          cellRenderer: (params: any) => (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm font-semibold">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {params.value?.toLocaleString() || 0}
            </span>
          ),
        });
      }

      // Add remaining columns
      cols.push(
        {
          field: 'compoundCount', headerName: 'Compounds', width: 120,
          sortable: true, filter: 'agNumberColumnFilter', type: 'numericColumn',
          headerTooltip: 'Registered compound records in this file; proteins and peptide sequence records are separate.',
          valueFormatter: (params) => formatDataCount(params.value),
        },
        {
          field: 'biologicalDataPointCount', headerName: 'Bio data points', width: 150,
          sortable: true, filter: 'agNumberColumnFilter', type: 'numericColumn',
          headerTooltip: DATA_POINT_DESCRIPTION,
          valueFormatter: (params) => formatDataCount(params.value),
        },
        {
          field: 'createdDate',
          headerName: 'Created',
          width: 150,
          sortable: true,
          valueFormatter: (params) =>
            params.value ? formatDate(params.value) : '-',
        },
        {
          field: 'modifiedDate',
          headerName: 'Modified',
          width: 150,
          sortable: true,
          valueFormatter: (params) =>
            params.value ? formatDate(params.value) : '-',
        },
        {
          field: 'moleculesLibraryCount',
          headerName: 'Rows',
          width: 100,
          sortable: true,
          type: 'numericColumn',
        },
        {
          field: 'propertiesCount',
          headerName: 'Columns',
          width: 100,
          sortable: true,
          type: 'numericColumn',
        },
        {
          field: 'moleculesInvalid',
          headerName: 'Failed',
          width: 100,
          sortable: true,
          type: 'numericColumn',
          cellRenderer: (params: any) =>
            params.value && params.value > 0 ? (
              <span className="text-red-500">{params.value}</span>
            ) : (
              <span className="text-gray-400">0</span>
            ),
        },
        {
          field: 'status',
          headerName: 'Status',
          width: 200,
          sortable: true,
          cellRenderer: (params: any) => {
            const status = params.value || 'ready';
            const data = params.data;

            // Show progress for processing files with cancel button
            if (status === 'processing' && data?.moleculesTotal > 0) {
              const processed = data.moleculesProcessed || 0;
              const total = data.moleculesTotal;
              const percent = Math.round((processed / total) * 100);
              const isCancelling = cancellingIds.has(data.id);
              return (
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-amber-600">Processing</span>
                      <span className="text-xs font-medium text-gray-500">{percent}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-300"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelUpload(data.id);
                    }}
                    disabled={isCancelling}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-50 transition-all"
                    title="Cancel upload"
                  >
                    {isCancelling ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </button>
                </div>
              );
            }

            // Status pill configurations
            const statusConfig: Record<string, { bg: string; text: string; icon: string; label: string }> = {
              ready: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: '●', label: 'Ready' },
              completed: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: '●', label: 'Ready' },
              processing: { bg: 'bg-amber-50', text: 'text-amber-700', icon: '◐', label: 'Processing' },
              failed: { bg: 'bg-red-50', text: 'text-red-600', icon: '●', label: 'Failed' },
              error: { bg: 'bg-red-50', text: 'text-red-600', icon: '●', label: 'Error' },
              cancelled: { bg: 'bg-gray-100', text: 'text-gray-600', icon: '●', label: 'Cancelled' },
            };

            const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-600', icon: '●', label: status };

            return (
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 ${config.bg} ${config.text} rounded-full text-xs font-semibold`}>
                <span className="text-[8px]">{config.icon}</span>
                {config.label}
              </span>
            );
          },
        }
      );

      return cols;
    },
    [showMatchCount, isFileLocked, cancellingIds, handleCancelUpload, onFileMoved, disabled]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
    }),
    []
  );

  const onGridReady = useCallback((params: GridReadyEvent) => {
    params.api.sizeColumnsToFit();
  }, []);

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedIds = selectedNodes.map((node) => node.data.id);
      setSelectedFileIds(selectedIds);
      if (onSelectionChange) {
        onSelectionChange(selectedIds);
      }
    },
    [setSelectedFileIds, onSelectionChange]
  );

  const onRowClicked = useCallback(
    (event: any) => {
      // Don't navigate if file is locked
      if (isFileLocked(event.data?.id)) {
        return;
      }

      // Check if click was on AG Grid's built-in checkbox
      const target = event.event?.target as HTMLElement;
      if (target?.closest('.ag-checkbox-input-wrapper') || target?.closest('.ag-selection-checkbox')) {
        return; // Don't navigate, checkbox handles its own click
      }

      // Navigate to editor
      if (searchQuery && searchType) {
        navigate(`/chemistry/file/${event.data.id}/editor`, {
          state: { searchQuery, searchType }
        });
      } else {
        navigate(`/chemistry/file/${event.data.id}/editor`);
      }
    },
    [navigate, searchQuery, searchType, isFileLocked]
  );

  // Style rows with alternating backgrounds and locked state
  const getRowStyle = useCallback((params: any) => {
    const baseStyle: any = {};

    // Alternating row backgrounds
    if (params.node.rowIndex % 2 === 0) {
      baseStyle.background = '#ffffff';
    } else {
      baseStyle.background = '#f9fafb';
    }

    // Locked files get opacity
    if (isFileLocked(params.data?.id)) {
      baseStyle.opacity = 0.5;
      baseStyle.cursor = 'not-allowed';
    }

    return baseStyle;
  }, [isFileLocked]);

  // Row class for hover effects
  const getRowClass = useCallback((params: any) => {
    if (isFileLocked(params.data?.id)) {
      return '';
    }
    return 'hover:bg-emerald-50/50 cursor-pointer transition-colors';
  }, [isFileLocked]);

  // Filter projects for move menu (exclude current project)
  const moveableProjects = useMemo(() => {
    return projects.filter(p => p.id !== currentProjectId);
  }, [projects, currentProjectId]);

  return (
    <div className={`ag-theme-alpine w-full h-full ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
      <style>{`
        .ag-theme-alpine {
          --ag-header-background-color: #f9fafb;
          --ag-header-foreground-color: #6b7280;
          --ag-border-color: #f3f4f6;
          --ag-row-border-color: transparent;
          --ag-selected-row-background-color: #ecfdf5;
          --ag-row-hover-color: #f0fdf4;
          --ag-font-family: inherit;
        }
        .ag-theme-alpine .ag-header-cell-label {
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .ag-theme-alpine .ag-cell {
          display: flex;
          align-items: center;
          border-right: none !important;
        }
        .ag-theme-alpine .ag-row {
          border-bottom: 1px solid #f3f4f6;
        }
        .ag-theme-alpine .ag-row:hover {
          background-color: #f0fdf4 !important;
        }
        .ag-theme-alpine .ag-row-selected {
          background-color: #ecfdf5 !important;
        }
        .ag-theme-alpine .ag-header {
          border-bottom: 2px solid #e5e7eb;
        }
        /* Style AG Grid built-in checkboxes */
        .ag-theme-alpine .ag-checkbox-input-wrapper {
          width: 20px;
          height: 20px;
        }
        .ag-theme-alpine .ag-checkbox-input-wrapper input {
          width: 20px;
          height: 20px;
          accent-color: #10b981;
          cursor: pointer;
        }
        .ag-theme-alpine .ag-checkbox-input-wrapper::after {
          color: #10b981;
        }
        .ag-theme-alpine .ag-checkbox-input-wrapper.ag-checked::after {
          color: #10b981;
        }
        /* Center checkbox in cell */
        .ag-theme-alpine .ag-selection-checkbox {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
      <AgGridReact
        ref={gridRef}
        theme="legacy"
        rowData={files}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowSelection={disabled ? undefined : "multiple"}
        suppressRowClickSelection={true}
        onGridReady={onGridReady}
        onSelectionChanged={disabled ? undefined : onSelectionChanged}
        onRowClicked={disabled ? undefined : onRowClicked}
        onCellContextMenu={disabled ? undefined : onCellContextMenu}
        getRowId={(params) => params.data.id}
        getRowStyle={getRowStyle}
        getRowClass={getRowClass}
        animateRows={true}
        rowHeight={56}
        headerHeight={48}
      />

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-white rounded-xl shadow-2xl border border-gray-100 py-2 min-w-[200px]"
          style={{ left: contextMenu.x, top: contextMenu.y, zIndex: 1100 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Move to submenu */}
          <div className="relative group">
            <button className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                Move to...
              </span>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Submenu */}
            <div className="absolute left-full top-0 ml-1 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 min-w-[180px] hidden group-hover:block">
              {isMoving ? (
                <div className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                  Moving...
                </div>
              ) : moveableProjects.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-400">No other projects</div>
              ) : (
                moveableProjects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => handleMoveToProject(project.id)}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-2"
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: project.color }}
                    />
                    {project.name}
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="border-t border-gray-100 my-1"></div>

          {/* Rename */}
          {isRenaming ? (
            <div className="px-3 py-2">
              <input
                ref={renameInputRef}
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenameSubmit();
                  if (e.key === 'Escape') handleRenameCancel();
                }}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Enter new name"
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleRenameSubmit}
                  className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600"
                >
                  Save
                </button>
                <button
                  onClick={handleRenameCancel}
                  className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={handleStartRename}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Rename
            </button>
          )}

          {/* Open in editor */}
          <button
            onClick={() => {
              navigate(`/chemistry/file/${contextMenu.fileId}/editor`);
              setContextMenu(null);
            }}
            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Open in Editor
          </button>

          <div className="border-t border-gray-100 my-1"></div>

          {/* Download as... submenu */}
          <div className="relative group/dl">
            <button className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download as...
              </span>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <div className="absolute left-full top-0 ml-1 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 min-w-[200px] hidden group-hover/dl:block">
              <button
                onClick={() => handleContextDownload(contextMenu.fileId, contextMenu.fileName, 'sdf')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center justify-between"
              >
                <span>SDF (Structure-Data)</span>
                <span className="text-xs text-gray-400">.sdf.gz</span>
              </button>
              <button
                onClick={() => handleContextDownload(contextMenu.fileId, contextMenu.fileName, 'csv')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center justify-between"
              >
                <span>CSV</span>
                <span className="text-xs text-gray-400">.csv</span>
              </button>
              <button
                onClick={() => handleContextDownload(contextMenu.fileId, contextMenu.fileName, 'smi')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center justify-between"
              >
                <span>SMILES</span>
                <span className="text-xs text-gray-400">.smi</span>
              </button>
            </div>
          </div>

          <div className="border-t border-gray-100 my-1"></div>

          {/* Clean Up Data */}
          <button
            onClick={() => {
              openDedupDialog(contextMenu.fileId);
              setContextMenu(null);
            }}
            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Clean Up Data
          </button>
        </div>
      )}
    </div>
  );
};

export default FileList;
